import React from 'react';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { Toaster } from 'sonner';

import { AppProvider, useApp } from '@/lib/store';
import QuickTour from '@/components/QuickTour';
import ExamWalkthrough from '@/components/ExamWalkthrough';
import AdminPortal from '@/pages/AdminPortal';
import AdminCenter from '@/pages/AdminCenter';
import AdminOps from '@/pages/AdminOps';
import AdminActivity from '@/pages/AdminActivity';
import AdminSpend from '@/pages/AdminSpend';
import Dashboard from '@/pages/Dashboard';
import DemoEntry from '@/pages/DemoEntry';
import Diagnostic from '@/pages/Diagnostic';
import ExemplarViewer from '@/pages/ExemplarViewer';
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import AcceptInvite from '@/pages/AcceptInvite';
import OrgPortal from '@/pages/OrgPortal';
import StudentLibraryPage from '@/pages/StudentLibraryPage';
import StudentProgressPage from '@/pages/StudentProgressPage';
import MyAssignmentsPage from '@/pages/MyAssignmentsPage';
import AssignmentDetailPage from '@/pages/AssignmentDetailPage';
import ExamRunner from '@/pages/ExamRunner';
import { LessonLibrary, LessonReader } from '@/pages/LessonViewer';
import MockRunner from '@/pages/MockRunner';
import MockReviewPage from '@/pages/MockReviewPage';
import PracticeReviewPage from '@/pages/PracticeReviewPage';
import SubmissionReviewPage from '@/pages/SubmissionReviewPage';
import MocksPage from '@/pages/MocksPage';
import ProgressPage from '@/pages/ProgressPage';
import PracticeHub from '@/pages/PracticeHub';
import ResultsPage from '@/pages/ResultsPage';
import SetPickerPage from '@/pages/SetPickerPage';
import WalkthroughPlayer from '@/pages/WalkthroughPlayer';
import PTEItemRunner from '@/pages/PTEItemRunner';
import PTEMockRunner from '@/pages/PTEMockRunner';
import PTESpeakingRunner from '@/pages/PTESpeakingRunner';
import SpeakingRunner from '@/pages/SpeakingRunner';
import WritingRunner from '@/pages/WritingRunner';

// The two exams have different real-exam screens; route to the right runner.
function PracticeRoute() {
  const { exam } = useParams();
  return exam === 'pte' ? <PTEItemRunner /> : <ExamRunner />;
}

function SpeakRoute() {
  const { exam } = useParams();
  return exam === 'pte' ? <PTESpeakingRunner /> : <SpeakingRunner />;
}

function MockRoute() {
  const { exam } = useParams();
  return exam === 'pte' ? <PTEMockRunner /> : <MockRunner />;
}

function Protected({ roles, children }) {
  const { user, loading } = useApp();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function Routed() {
  const { user, loading } = useApp();
  if (loading) return null;
  return (
    <>
      <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/demo/:token" element={<DemoEntry />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/" element={user ? <Dashboard /> : <Landing />} />
      <Route
        path="/:exam/diagnostic"
        element={
          <Protected>
            <Diagnostic />
          </Protected>
        }
      />
      <Route
        path="/:exam/lessons"
        element={
          <Protected>
            <LessonLibrary />
          </Protected>
        }
      />
      <Route
        path="/:exam/lessons/:unitId"
        element={
          <Protected>
            <LessonReader />
          </Protected>
        }
      />
      <Route
        path="/:exam/walkthrough/:walkthroughId"
        element={
          <Protected>
            <WalkthroughPlayer />
          </Protected>
        }
      />
      <Route
        path="/:exam/practice"
        element={
          <Protected>
            <PracticeHub />
          </Protected>
        }
      />
      <Route
        path="/:exam/sets/:taskType"
        element={
          <Protected>
            <SetPickerPage />
          </Protected>
        }
      />
      <Route
        path="/:exam/practice/:taskType"
        element={
          <Protected>
            <PracticeRoute />
          </Protected>
        }
      />
      <Route
        path="/:exam/models/:taskType"
        element={
          <Protected>
            <ExemplarViewer />
          </Protected>
        }
      />
      <Route
        path="/:exam/write/:taskType"
        element={
          <Protected>
            <WritingRunner />
          </Protected>
        }
      />
      <Route
        path="/:exam/mocks"
        element={
          <Protected>
            <MocksPage />
          </Protected>
        }
      />
      <Route
        path="/:exam/exam-walkthrough"
        element={
          <Protected>
            <ExamWalkthrough />
          </Protected>
        }
      />
      <Route
        path="/:exam/progress"
        element={
          <Protected>
            <ProgressPage />
          </Protected>
        }
      />
      <Route
        path="/:exam/mock/:form"
        element={
          <Protected>
            <MockRoute />
          </Protected>
        }
      />
      <Route
        path="/:exam/mock/:form/review"
        element={
          <Protected>
            <MockReviewPage />
          </Protected>
        }
      />
      <Route
        path="/:exam/results"
        element={
          <Protected>
            <ResultsPage />
          </Protected>
        }
      />
      <Route path="/:exam/submissions" element={<Navigate to="../results" replace />} />
      <Route
        path="/:exam/submissions/:submission_id/review"
        element={
          <Protected>
            <SubmissionReviewPage />
          </Protected>
        }
      />
      <Route
        path="/:exam/practice-attempts/:attempt_id/review"
        element={
          <Protected>
            <PracticeReviewPage />
          </Protected>
        }
      />
      <Route
        path="/:exam/speak/:taskType"
        element={
          <Protected>
            <SpeakRoute />
          </Protected>
        }
      />
      <Route
        path="/org"
        element={
          <Protected roles={['instructor', 'co_admin', 'org_admin', 'platform_admin']}>
            <OrgPortal />
          </Protected>
        }
      />
      <Route
        path="/org/students/:user_id/library"
        element={
          <Protected roles={['instructor', 'co_admin', 'org_admin', 'platform_admin']}>
            <StudentLibraryPage />
          </Protected>
        }
      />
      <Route
        path="/org/students/:user_id/progress"
        element={
          <Protected roles={['instructor', 'co_admin', 'org_admin', 'platform_admin']}>
            <StudentProgressPage />
          </Protected>
        }
      />
      <Route
        path="/assignments"
        element={
          <Protected>
            <MyAssignmentsPage />
          </Protected>
        }
      />
      <Route
        path="/assignments/:assignment_id"
        element={
          <Protected>
            <AssignmentDetailPage />
          </Protected>
        }
      />
      <Route
        path="/admin"
        element={
          <Protected roles={['platform_admin']}>
            <AdminPortal />
          </Protected>
        }
      />
      <Route
        path="/admin/centers/:orgId"
        element={
          <Protected roles={['platform_admin']}>
            <AdminCenter />
          </Protected>
        }
      />
      <Route
        path="/admin/ops"
        element={
          <Protected roles={['platform_admin']}>
            <AdminOps />
          </Protected>
        }
      />
      <Route
        path="/admin/activity"
        element={
          <Protected roles={['platform_admin']}>
            <AdminActivity />
          </Protected>
        }
      />
      <Route
        path="/admin/spend"
        element={
          <Protected roles={['platform_admin']}>
            <AdminSpend />
          </Protected>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {user && user.exams && user.exams.length > 0 && (
        <QuickTour user={user} exam={user.exams[0]} />
      )}
    </>
  );
}

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Routed />
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AppProvider>
  );
}
